import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Flame, Trophy, Crown, Medal, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useStreaks } from "@/hooks/useParticipantData";
import { useSelectedCohort } from "@/hooks/useSelectedCohort";

interface StreakUser {
  id: string;
  name: string;
  avatar: string;
  streak: number;
  rank: number;
}

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1: return <Crown className="w-4 h-4 text-amber-400" />;
    case 2: return <Medal className="w-4 h-4 text-slate-400" />;
    case 3: return <Medal className="w-4 h-4 text-amber-600" />;
    default: return <span className="text-xs font-bold text-muted-foreground">#{rank}</span>;
  }
};

/** Racha personal (autoaprendizaje o sin ruta): sin ranking, sin comparación. */
function PersonalStreakCard() {
  const navigate = useNavigate();
  const { data: streaks = [] } = useStreaks();
  const personal = streaks.find(s => s.scope_type !== 'cohort');
  const current = personal?.current_streak ?? 0;
  const best = personal?.max_streak ?? 0;

  return (
    <Card className="glass-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flame className="w-5 h-5 text-orange-500" />
            Tu racha
          </CardTitle>
          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => navigate('/profile?tab=gamificacion')}>
            Ver todo
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-foreground">{current}</span>
            <span className="text-sm text-muted-foreground">días seguidos</span>
          </div>
          <div className="ml-auto text-right">
            <p className="text-xs text-muted-foreground">Tu mejor racha</p>
            <p className="text-sm font-semibold text-foreground">{best} días</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          En rutas de autoaprendizaje tu racha es personal: no se compara con otros participantes.
          Tu reto es superar tu mejor racha.
        </p>
      </CardContent>
    </Card>
  );
}

export function StreakRankingWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEnrollment, isLoading: loadingEnrollments } = useSelectedCohort();

  // Solo las rutas con acompañamiento (cohortes no perpetuas) tienen ranking.
  // Las cohortes vistas como mentor (solo lectura) no cuentan.
  const guidedCohort =
    activeEnrollment && !activeEnrollment.is_perpetual && !activeEnrollment.view_only
      ? activeEnrollment
      : null;
  const cohortId = guidedCohort?.cohort_id;

  const { data: ranking = [], isLoading } = useQuery({
    queryKey: ['streak-ranking', cohortId],
    queryFn: async () => {
      if (!cohortId) return [] as StreakUser[];

      const { data, error } = await (supabase as any).rpc('get_cohort_streak_ranking', {
        _cohort_id: cohortId,
        _limit: 10,
      });
      if (error) throw error;

      const ranked: StreakUser[] = ((data ?? []) as any[]).map((s: any, i: number) => ({
        id: s.user_id,
        name: s.full_name || 'Participante',
        avatar: s.avatar_url || '',
        streak: s.current_streak ?? 0,
        rank: i + 1,
      }));

      // If current user is not in top results, add them
      if (user && !ranked.find(r => r.id === user.id)) {
        const { data: myStreak } = await supabase
          .from('participant_streaks')
          .select('current_streak')
          .eq('user_id', user.id)
          .eq('scope_type', 'cohort')
          .eq('scope_id', cohortId)
          .maybeSingle();

        const { data: myProfile } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('id', user.id)
          .maybeSingle();

        const myStreakVal = myStreak?.current_streak ?? 0;
        const rank = ranked.filter(r => r.streak > myStreakVal).length + 1;

        ranked.push({
          id: user.id,
          name: 'Tú',
          avatar: myProfile?.avatar_url || '',
          streak: myStreakVal,
          rank,
        });
      } else {
        const me = ranked.find(r => r.id === user?.id);
        if (me) me.name = 'Tú';
      }

      const top3 = ranked.slice(0, 3);
      const me = ranked.find(r => r.id === user?.id);
      if (me && !top3.find(r => r.id === user?.id)) {
        return [...top3, me];
      }
      return top3;
    },
    enabled: !!user && !!cohortId,
  });

  if (loadingEnrollments) return null;
  if (!cohortId) return <PersonalStreakCard />;

  return (
    <Card className="glass-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-amber-400" />
            Ranking de Rachas
          </CardTitle>
          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => navigate('/profile?tab=gamificacion')}>
            Ver todo
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground pt-1">
          Te comparas con los participantes de tu cohorte "{guidedCohort?.cohort_name}"
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : ranking.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay datos de rachas aún
          </p>
        ) : (
          <div className="space-y-2">
            {ranking.map((u) => (
              <div
                key={u.id}
                className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                  u.name === 'Tú' ? 'bg-primary/10 border border-primary/30' : 'bg-background/50'
                }`}
              >
                <div className="w-6 flex justify-center shrink-0">{getRankIcon(u.rank)}</div>
                <Avatar className="w-8 h-8 shrink-0">
                  <AvatarImage src={u.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {u.name.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    u.name === 'Tú' ? 'text-primary' : 'text-foreground'
                  }`}>{u.name}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-bold text-foreground">{u.streak}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
